import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/api';
import type { components } from '@/api/qraft/schema';
import { LoadingSpinner } from '@/common/LoadingSpinner';
import { type HelpPlacement, useHelpLanguage } from '~/hooks/useHelpPlacements';
import {
  ContentArea,
  ModalBody,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  Overlay,
  SideNav,
  SideNavCategory,
  SideNavItem,
} from './HelpModal.styles';
import { ModalCloseButton } from './ModalCloseButton';

type ContextualHelpFrame = components['schemas']['ContextualHelpFrame'];

export interface HelpModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * The screen's help anchor. Absent for the app bar's global `?`, which opens
   * the whole tree filtered to the viewer's profile type; only that read is
   * audience-filtered, as in legacy.
   */
  placement?: HelpPlacement;
}

/**
 * The contextual help popup.
 *
 * An anchored `?` fetches one section and the global `?` fetches the tree. Both
 * return the same `ContextualHelpFrame`, so one renderer handles both and the
 * side nav renders every section it is given.
 */
export function HelpModal({ isOpen, onClose, placement }: HelpModalProps) {
  // The branch components are what hold the queries, so nothing is in flight
  // while the modal is shut.
  if (!isOpen) return null;

  return placement === undefined ? (
    <HelpTree onClose={onClose} />
  ) : (
    <HelpAnchor placement={placement} onClose={onClose} />
  );
}

function HelpAnchor({
  placement,
  onClose,
}: {
  placement: HelpPlacement;
  onClose: () => void;
}) {
  const language = useHelpLanguage();
  // No profile-type filter here: only the tree is audience-filtered, so a
  // screen's own help is whatever it anchors. A placement with no section in
  // `language` 404s and shows the empty state below.
  const { data, isPending } = api.getContextualHelpPlacement.useQuery({
    path: { placement },
    query: { language },
  });

  return <HelpFrame frame={data} isPending={isPending} onClose={onClose} />;
}

function HelpTree({ onClose }: { onClose: () => void }) {
  const language = useHelpLanguage();
  const { data, isPending } = api.getContextualHelpTree.useQuery({
    query: { language },
  });

  return <HelpFrame frame={data} isPending={isPending} onClose={onClose} />;
}

function HelpFrame({
  frame,
  isPending,
  onClose,
}: {
  frame: ContextualHelpFrame | undefined;
  isPending: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const sections = frame?.sections ?? [];
  // Flattened in render order, because legacy auto-selects the first article in
  // the DOM, skipping any leading section with no articles. Empty sections
  // still render their heading, as in legacy.
  const articles = sections.flatMap((section) => section.articles);

  // Derived, not an effect: the payload lands after the first render, and a
  // selection stored in state would have to be re-synced on every arrival.
  const selected = articles.find((a) => a.id === selectedId) ?? articles[0];

  return (
    <Overlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>{frame?.popup_title ?? ''}</ModalTitle>
          <ModalCloseButton onClick={onClose} />
        </ModalHeader>

        <ModalBody>
          <SideNav>
            {sections.map((section) => (
              // A fragment rather than a wrapper, so `SideNavCategory`'s
              // `:first-child` rule still means the first section.
              <Fragment key={section.id}>
                <SideNavCategory>{section.name}</SideNavCategory>
                {section.articles.map((article) => (
                  <SideNavItem
                    key={article.id}
                    $active={selected?.id === article.id}
                    // The DOM half of the transient `$active` prop, which
                    // styled-components doesn't emit, so tests can assert which
                    // row the auto-select picked.
                    data-active={selected?.id === article.id ? 'true' : 'false'}
                    onClick={() => setSelectedId(article.id)}
                  >
                    {article.title}
                  </SideNavItem>
                ))}
              </Fragment>
            ))}
          </SideNav>

          {selected ? (
            <ContentArea
              // biome-ignore lint/security/noDangerouslySetInnerHtml: migrated admin-authored bodies, paragraph-wrapped server-side (`ContextualHelpArticleItem.content` is `MediaHtml`). Measured over all 349 published bodies: zero img, iframe, script, style, class or shortcode; 29 carry an anchor and 28 of those are mailto.
              dangerouslySetInnerHTML={{ __html: selected.content }}
            />
          ) : (
            <ContentArea>
              {isPending ? (
                <LoadingSpinner centered />
              ) : (
                // Legacy's empty-state string; untranslated in ES and FR, as in
                // legacy.
                t('wbs.help-no-content')
              )}
            </ContentArea>
          )}
        </ModalBody>
      </ModalContainer>
    </Overlay>
  );
}
